import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowClockwise, MagnifyingGlass, Trash } from "@phosphor-icons/react";

export default function SearchMissesPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/search-misses", { params: { days, limit: 100 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (q) => {
    try {
      await api.delete(`/admin/search-misses/${encodeURIComponent(q)}`);
      toast.success(`Requête « ${q} » archivée`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="admin-search-misses">
      <div className="bg-card border border-border rounded-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <div className="label-uppercase mb-1">Recherches sans résultat</div>
            <div className="font-display text-lg font-medium">Requêtes des visiteurs sans réponse</div>
            <div className="text-xs text-muted-foreground mt-1">
              Identifie les besoins non couverts. Ajoute un mapping NAF dans <code className="bg-accent/40 px-1">naf_labels.py</code> ou importe les entreprises manquantes pour combler le gap.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="text-sm bg-background border border-border rounded-sm px-3 py-2" data-testid="search-misses-days">
              <option value={7}>7 derniers jours</option>
              <option value={30}>30 derniers jours</option>
              <option value={90}>90 derniers jours</option>
            </select>
            <button onClick={load} disabled={loading} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-sm font-semibold disabled:opacity-60" data-testid="search-misses-refresh">
              <ArrowClockwise size={14} className={loading ? "animate-spin" : ""}/>
            </button>
          </div>
        </div>

        {!data ? (
          <div className="text-sm text-muted-foreground">Chargement…</div>
        ) : data.items.length === 0 ? (
          <div className="bg-accent/20 border border-dashed border-border rounded-sm p-12 text-center">
            <MagnifyingGlass size={28} className="mx-auto text-muted-foreground mb-2"/>
            <div className="text-sm font-semibold">Aucune recherche vide sur cette période 🎉</div>
            <div className="text-xs text-muted-foreground mt-1">Toutes les requêtes des visiteurs trouvent au moins un résultat.</div>
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
              <span className="font-semibold text-foreground tabular-nums">{data.total_distinct}</span>
              requêtes distinctes sans résultat sur {data.days} jours
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-4 py-2">Requête</th>
                    <th className="px-4 py-2 text-right">Occurrences</th>
                    <th className="px-4 py-2">Dernière vue</th>
                    <th className="px-4 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.items.map((it) => (
                    <tr key={it.query} className="hover:bg-accent/30" data-testid={`miss-row-${it.query}`}>
                      <td className="px-4 py-2 font-medium">« {it.query} »</td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">{it.count}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{it.last_seen ? new Date(it.last_seen).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                      <td className="px-4 py-2">
                        <button onClick={() => dismiss(it.query)} className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm text-muted-foreground" title="Archiver" data-testid={`miss-dismiss-${it.query}`}>
                          <Trash size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
